/**
 * @fileoverview Deposits Portfolio hub page.
 * @description Two clickable cards linking to this group's sub-pages (Cost of Deposit
 * Composition, Deposit Averages & Productivity), each previewing that sub-page's own headline
 * metric and YoY trend. Ported from the InsightBox Banking design system's own
 * `DepositsPortfolio/Grid.tsx` — see `BankingMembershipPage.tsx` for the shared local
 * `fy`/`branchId` filter-state convention this page (and every other banking hub) follows instead
 * of the reference's global FilterContext.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useCurrentSession } from '@/features/auth'
import {
  LATEST_FY,
  depositAverages,
  formatCr,
  formatRupee,
  formatSignedPct,
  resolveBankingAccessScope,
  resolveFy,
  totalInterestPaid,
  trendLabel,
  useBankingData,
  yoyGrowth,
} from '@/features/banking'
import PageHeader from '@/shared/components/molecules/PageHeader'
import ViewGridCard from '@/shared/components/molecules/ViewGridCard'
import { ROUTES } from '@/shared/constants/routes.constants'

import type { FyFilter } from '@/features/banking'

/**
 * @component
 */
function BankingDepositsPage() {
  const { t } = useTranslation()
  const dataQuery = useBankingData()
  const [fyFilter] = useState<FyFilter>(LATEST_FY)
  const bankingScope = resolveBankingAccessScope(useCurrentSession().data?.bankingScope)
  const [branchId] = useState(bankingScope.defaultBranchId)

  const header = (
    <PageHeader
      kicker={t('banking.deposits.kicker')}
      title={t('nav.depositsPortfolio')}
      description={t('banking.deposits.description')}
    />
  )

  if (dataQuery.isPending) {
    return (
      <div className="flex flex-col gap-4">
        {header}
        <div className="grid grid-cols-[repeat(auto-fit,minmax(min(320px,100%),1fr))] gap-5">
          <ViewGridCard
            to={ROUTES.BANKING_DEPOSITS_COST_OF_DEPOSIT}
            title=""
            description=""
            metricLabel=""
            metricValue=""
            trend={{ direction: 'flat', text: '' }}
            loading
          />
          <ViewGridCard
            to={ROUTES.BANKING_DEPOSITS_AVERAGES}
            title=""
            description=""
            metricLabel=""
            metricValue=""
            trend={{ direction: 'flat', text: '' }}
            loading
          />
        </div>
      </div>
    )
  }

  if (dataQuery.isError) {
    return (
      <div className="flex flex-col gap-4">
        {header}
        <p className="text-sm text-danger">{t('banking.loadError')}</p>
      </div>
    )
  }

  const data = dataQuery.data
  const fy = resolveFy(fyFilter)
  const interestGrowth = yoyGrowth(fy, (f) => totalInterestPaid(data, f, branchId))
  const avgGrowth = yoyGrowth(fy, (f) => depositAverages(data, f, branchId).avgPerAccount)

  return (
    <div className="flex flex-col gap-4">
      {header}
      <div className="grid grid-cols-[repeat(auto-fit,minmax(min(320px,100%),1fr))] gap-5">
        <ViewGridCard
          to={ROUTES.BANKING_DEPOSITS_COST_OF_DEPOSIT}
          title={t('banking.deposits.costOfDeposit.title')}
          description={t('banking.deposits.costOfDeposit.description')}
          metricLabel={t('banking.deposits.costOfDeposit.metricLabel')}
          metricValue={formatCr(totalInterestPaid(data, fy, branchId))}
          trend={{
            direction: trendLabel(interestGrowth),
            text:
              interestGrowth !== null
                ? t('banking.yoyTrend', { value: formatSignedPct(interestGrowth) })
                : t('banking.baseYear'),
            positiveIsGood: false,
          }}
        />
        <ViewGridCard
          to={ROUTES.BANKING_DEPOSITS_AVERAGES}
          title={t('banking.deposits.depositAverages.title')}
          description={t('banking.deposits.depositAverages.description')}
          metricLabel={t('banking.deposits.depositAverages.metricLabel')}
          metricValue={formatRupee(depositAverages(data, fy, branchId).avgPerAccount)}
          trend={{
            direction: trendLabel(avgGrowth),
            text:
              avgGrowth !== null
                ? t('banking.yoyTrend', { value: formatSignedPct(avgGrowth) })
                : t('banking.baseYear'),
          }}
        />
      </div>
    </div>
  )
}

export default BankingDepositsPage
